import React from 'react';
import { Package, ClipboardList, Barcode, Box } from 'lucide-react';

const IsometricInventoryManagement = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                {/* Standard Purple Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle grid pattern */}
                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                            }}
                        />
                    </div>
                </div>

                {/* --- Elements on Base --- */}

                {/* Stack of Boxes (Back Left) */}
                <div className="absolute" style={{ left: '40px', top: '20px', transform: 'translateZ(0px)', transformStyle: 'preserve-3d' }}>
                    {/* Bottom Box */}
                    <div className="absolute w-16 h-16" style={{ transform: 'translateZ(0px)', transformStyle: 'preserve-3d' }}>
                        {Array.from({ length: 16 }).map((_, i) => (
                            <div key={i} className="absolute inset-0 bg-amber-700 rounded-sm" style={{ transform: `translateZ(${i}px)` }} />
                        ))}
                        <div className="absolute inset-0 bg-amber-500 rounded-sm border border-amber-400 flex items-center justify-center p-2" style={{ transform: 'translateZ(16px)' }}>
                            <Box className="w-8 h-8 text-amber-800 opacity-50" />
                        </div>
                    </div>
                    {/* Top Box (Offset) */}
                    <div className="absolute w-14 h-14" style={{ left: '5px', top: '5px', transform: 'translateZ(16px)', transformStyle: 'preserve-3d' }}>
                        {Array.from({ length: 14 }).map((_, i) => (
                            <div key={i} className="absolute inset-0 bg-amber-700 rounded-sm" style={{ transform: `translateZ(${i}px)` }} />
                        ))}
                        <div className="absolute inset-0 bg-amber-400 rounded-sm border border-amber-300 flex items-center justify-center" style={{ transform: 'translateZ(14px)' }}>
                            <div className="w-8 h-1 bg-amber-600/30 rounded"></div>
                        </div>
                    </div>
                </div>

                {/* Inventory Dashboard / List (Standing Vertical) */}
                <div
                    className="absolute"
                    style={{
                        right: '40px',
                        top: '-190px',
                        width: '140px',
                        height: '180px',
                        transform: 'translateZ(20px) rotateX(-90deg) rotateY(0deg)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-gray-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Screen Face */}
                    <div className="absolute inset-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden flex flex-col p-3">
                        {/* Header */}
                        <div className="flex items-center gap-2 border-b border-gray-100 pb-2 mb-2">
                            <ClipboardList className="w-4 h-4 text-teal-500" />
                            <div className="h-2 w-16 bg-gray-200 rounded"></div>
                        </div>

                        {/* List Items */}
                        <div className="space-y-2">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <div className="w-12 h-1.5 bg-gray-200 rounded"></div>
                                        <div className="w-8 h-1.5 bg-gray-100 rounded"></div>
                                    </div>
                                    {/* Stock Level Bar */}
                                    <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${i % 2 === 0 ? 'bg-teal-400' : 'bg-orange-400'}`}
                                            style={{ width: `${60 + (i * 10)}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Bottom Stats */}
                        <div className="mt-auto grid grid-cols-2 gap-2">
                            <div className="h-8 bg-teal-50 rounded flex flex-col justify-center items-center">
                                <span className="text-[8px] text-teal-600 font-bold">IN</span>
                            </div>
                            <div className="h-8 bg-orange-50 rounded flex flex-col justify-center items-center">
                                <span className="text-[8px] text-orange-600 font-bold">OUT</span>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Floating Scanner / Package Icon (Top Right) */}
                <div
                    className="absolute"
                    style={{
                        left: '40px',
                        bottom: '40px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(90px)',
                        transformStyle: 'preserve-3d',
                        animation: 'float-y 3s ease-in-out infinite'
                    }}
                >
                    <style>
                        {`@keyframes float-y { 0%, 100% { transform: translateZ(90px); } 50% { transform: translateZ(110px); } }`}
                    </style>
                    {/* Thickness */}
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-teal-700" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-teal-500 rounded-2xl border border-teal-400 shadow-lg flex items-center justify-center">
                        <Barcode className="w-8 h-8 text-white" />
                    </div>
                </div>

                {/* Medicine Vial / Bottle (Bottom Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '60px',
                        bottom: '60px',
                        width: '30px',
                        height: '30px',
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Cylinder Body (Rough approximation with stacked squares for now, or simplified to a boxy bottle) */}
                    <div className="absolute w-8 h-8" style={{ transform: 'translateZ(0px)', transformStyle: 'preserve-3d' }}>
                        {/* Bottle Body */}
                        {Array.from({ length: 25 }).map((_, i) => (
                            <div key={i} className="absolute inset-0 bg-orange-200 rounded-full" style={{ transform: `translateZ(${i}px)` }} />
                        ))}
                        {/* Cap */}
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="absolute inset-0 bg-white rounded-full scale-75" style={{ transform: `translateZ(${25 + i}px)` }} />
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IsometricInventoryManagement;
